import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import {
  AdminButtonComponent,
  AdminCardComponent,
  AdminEmptyStateComponent,
  AdminIconComponent,
  AdminModalComponent,
  AdminTableComponent,
} from '../../admin';
import { AdminConfirmDialogService } from '../../admin/admin-confirm-dialog.service';
import { AdminToastService } from '../../admin/admin-toast.service';
import { ApplicantDocumentsUploadModalComponent } from '../applicant-documents-upload-modal/applicant-documents-upload-modal.component';
import type { ApplicantDetailArea } from '../../services/applicant-notes.service';
import {
  ApplicantDocumentsService,
  type ApplicantDocumentDto,
  type UploadApplicantDocumentItem,
} from '../../services/applicant-documents.service';

const PREVIEW_DOCUMENT_LIMIT = 3;

@Component({
  selector: 'app-applicant-documents-section',
  standalone: true,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    AdminCardComponent,
    AdminButtonComponent,
    AdminEmptyStateComponent,
    AdminIconComponent,
    AdminModalComponent,
    AdminTableComponent,
    ApplicantDocumentsUploadModalComponent,
  ],
  template: `
    <app-admin-card>
      <div class="documents-section__head">
        <h3 class="documents-section__title">
          <app-admin-icon name="file-text" [size]="18" />
          Documents
          @if (documents().length > 0) {
            <span class="documents-section__count">({{ documents().length }})</span>
          }
        </h3>
        @if (!isArchived()) {
          <app-admin-button variant="primary" type="button" (click)="openUploadModal()">
            <app-admin-icon name="plus" [size]="16" />
            Upload Documents
          </app-admin-button>
        }
      </div>

      @if (loading()) {
        <p class="cg-form-help">Loading documents…</p>
      } @else if (documents().length === 0) {
        <app-admin-empty-state
          icon="file-text"
          title="No documents yet"
          description="Upload applicant-related files such as passport copies, transcripts, or visa documents."
        />
      } @else {
        <app-admin-table>
          <thead>
            <tr>
              <th>Document name</th>
              <th>Category</th>
              <th>Original file</th>
              <th>Size</th>
              <th>Uploaded by</th>
              <th>Upload date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            @for (doc of previewDocuments(); track doc.id) {
              <tr>
                <td class="documents-section__name">{{ doc.documentName }}</td>
                <td>{{ doc.category || '—' }}</td>
                <td class="documents-section__file-name documents-section__file-name--compact">
                  {{ doc.originalFileName }}
                </td>
                <td>{{ formatFileSize(doc.fileSize) }}</td>
                <td>{{ doc.uploadedByDisplayName || doc.uploadedBy }}</td>
                <td>{{ (doc.uploadedAt || doc.createdAt) | date: 'medium' }}</td>
                <td>
                  <ng-container
                    *ngTemplateOutlet="documentActions; context: { $implicit: doc }"
                  />
                </td>
              </tr>
            }
          </tbody>
        </app-admin-table>

        @if (showViewAllButton()) {
          <div class="documents-section__view-all">
            <app-admin-button variant="secondary" type="button" (click)="openViewAllModal()">
              View all ({{ documents().length }})
            </app-admin-button>
          </div>
        }
      }
    </app-admin-card>

    @if (viewAllModalOpen()) {
      <app-admin-modal title="All documents" size="xl" (closed)="closeViewAllModal()">
        <div class="documents-section__modal">
          <p class="documents-section__modal-summary">
            {{ documents().length }} document{{ documents().length === 1 ? '' : 's' }} uploaded
          </p>
          <div class="documents-section__modal-table">
            <app-admin-table>
              <thead>
                <tr>
                  <th>Document name</th>
                  <th>Category</th>
                  <th>Original file</th>
                  <th>Size</th>
                  <th>Uploaded by</th>
                  <th>Upload date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                @for (doc of documents(); track doc.id) {
                  <tr>
                    <td class="documents-section__name">{{ doc.documentName }}</td>
                    <td>{{ doc.category || '—' }}</td>
                    <td class="documents-section__file-name">{{ doc.originalFileName }}</td>
                    <td>{{ formatFileSize(doc.fileSize) }}</td>
                    <td>{{ doc.uploadedByDisplayName || doc.uploadedBy }}</td>
                    <td>{{ (doc.uploadedAt || doc.createdAt) | date: 'medium' }}</td>
                    <td>
                      <ng-container
                        *ngTemplateOutlet="documentActions; context: { $implicit: doc }"
                      />
                    </td>
                  </tr>
                }
              </tbody>
            </app-admin-table>
          </div>
        </div>
      </app-admin-modal>
    }

    <ng-template #documentActions let-doc>
      <div class="cg-admin-table-actions">
        <app-admin-button
          variant="ghost"
          type="button"
          [disabled]="actionLoadingId() === doc.id"
          (click)="viewDocument(doc)"
        >
          View
        </app-admin-button>
        <app-admin-button
          variant="secondary"
          type="button"
          [disabled]="actionLoadingId() === doc.id"
          (click)="downloadDocument(doc)"
        >
          Download
        </app-admin-button>
        @if (canDelete(doc)) {
          <app-admin-button
            variant="danger"
            type="button"
            [disabled]="actionLoadingId() === doc.id || isArchived()"
            (click)="confirmDelete(doc)"
          >
            Delete
          </app-admin-button>
        }
      </div>
    </ng-template>

    <app-applicant-documents-upload-modal
      [open]="uploadModalOpen()"
      [busy]="uploading()"
      [error]="uploadError()"
      (confirmed)="onUploadConfirmed($event)"
      (closed)="closeUploadModal()"
    />
  `,
  styles: `
    .documents-section__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--cg-space-3);
      margin-bottom: var(--cg-space-4);
    }
    .documents-section__title {
      display: flex;
      align-items: center;
      gap: var(--cg-space-2);
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--cg-color-text);
    }
    .documents-section__count {
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--cg-color-text-muted);
    }
    .documents-section__name {
      max-width: 12rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .documents-section__file-name {
      max-width: 14rem;
      word-break: break-word;
    }
    .documents-section__file-name--compact {
      max-width: 10rem;
      word-break: normal;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .documents-section__view-all {
      display: flex;
      justify-content: center;
      margin-top: var(--cg-space-4);
    }
    .documents-section__modal {
      display: flex;
      flex-direction: column;
      gap: var(--cg-space-3);
    }
    .documents-section__modal-summary {
      margin: 0;
      font-size: 0.875rem;
      color: var(--cg-color-text-muted);
    }
    .documents-section__modal-table {
      max-height: min(70vh, 36rem);
      overflow: auto;
    }
  `,
})
export class ApplicantDocumentsSectionComponent implements OnInit {
  readonly applicantId = input.required<string>();
  readonly area = input.required<ApplicantDetailArea>();
  readonly isArchived = input(false);
  readonly isAdmin = input(false);
  readonly currentUserId = input<string | null>(null);

  readonly changed = output<void>();

  private readonly documentsService = inject(ApplicantDocumentsService);
  private readonly confirmDialog = inject(AdminConfirmDialogService);
  private readonly toast = inject(AdminToastService);

  readonly documents = signal<ApplicantDocumentDto[]>([]);
  readonly loading = signal(false);
  readonly uploading = signal(false);
  readonly uploadModalOpen = signal(false);
  readonly viewAllModalOpen = signal(false);
  readonly uploadError = signal<string | null>(null);
  readonly actionLoadingId = signal<string | null>(null);

  readonly previewDocuments = computed(() =>
    this.documents().slice(0, PREVIEW_DOCUMENT_LIMIT),
  );

  readonly showViewAllButton = computed(() => this.documents().length > PREVIEW_DOCUMENT_LIMIT);

  ngOnInit(): void {
    this.loadDocuments();
  }

  reload(): void {
    this.loadDocuments();
  }

  openUploadModal(): void {
    this.uploadError.set(null);
    this.uploadModalOpen.set(true);
  }

  closeUploadModal(): void {
    if (this.uploading()) return;
    this.uploadModalOpen.set(false);
    this.uploadError.set(null);
  }

  openViewAllModal(): void {
    this.viewAllModalOpen.set(true);
  }

  closeViewAllModal(): void {
    this.viewAllModalOpen.set(false);
  }

  onUploadConfirmed(payload: { files: File[]; items: UploadApplicantDocumentItem[] }): void {
    const applicantId = this.applicantId();
    if (!applicantId) return;

    this.uploading.set(true);
    this.uploadError.set(null);

    this.documentsService.upload(this.area(), applicantId, payload.files, payload.items).subscribe({
      next: (created) => {
        this.documents.update((current) => [...created, ...current]);
        this.uploading.set(false);
        this.uploadModalOpen.set(false);
        this.toast.success(
          created.length === 1 ? 'Document uploaded.' : `${created.length} documents uploaded.`,
        );
        this.changed.emit();
      },
      error: (err) => {
        this.uploading.set(false);
        const msg = err?.error?.message ?? 'Could not upload documents.';
        this.uploadError.set(msg);
        this.toast.error(msg);
      },
    });
  }

  viewDocument(doc: ApplicantDocumentDto): void {
    this.accessDocument(doc, 'view', (url) => {
      window.open(url, '_blank', 'noopener,noreferrer');
    });
  }

  downloadDocument(doc: ApplicantDocumentDto): void {
    this.accessDocument(doc, 'download', (url) => {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = doc.originalFileName || doc.documentName;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    });
  }

  async confirmDelete(doc: ApplicantDocumentDto): Promise<void> {
    const confirmed = await this.confirmDialog.confirm({
      title: 'Delete document',
      message: `Delete "${doc.documentName}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    const applicantId = this.applicantId();
    if (!applicantId) return;

    this.actionLoadingId.set(doc.id);
    this.documentsService.delete(this.area(), applicantId, doc.id).subscribe({
      next: () => {
        this.documents.update((rows) => rows.filter((row) => row.id !== doc.id));
        this.actionLoadingId.set(null);
        if (this.documents().length <= PREVIEW_DOCUMENT_LIMIT) {
          this.viewAllModalOpen.set(false);
        }
        this.toast.success('Document deleted.');
        this.changed.emit();
      },
      error: (err) => {
        this.actionLoadingId.set(null);
        const msg = err?.error?.message ?? 'Could not delete document.';
        this.toast.error(msg);
      },
    });
  }

  canDelete(doc: ApplicantDocumentDto): boolean {
    if (this.isAdmin()) return true;
    const userId = this.currentUserId();
    return Boolean(userId && doc.uploadedBy === userId);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private accessDocument(
    doc: ApplicantDocumentDto,
    intent: 'view' | 'download',
    onUrl: (url: string) => void,
  ): void {
    const applicantId = this.applicantId();
    if (!applicantId) return;

    this.actionLoadingId.set(doc.id);
    this.documentsService.access(this.area(), applicantId, doc.id, intent).subscribe({
      next: ({ url }) => {
        this.actionLoadingId.set(null);
        onUrl(url);
      },
      error: (err) => {
        this.actionLoadingId.set(null);
        const msg = err?.error?.message ?? `Could not ${intent} document.`;
        this.toast.error(msg);
      },
    });
  }

  private loadDocuments(): void {
    const applicantId = this.applicantId();
    if (!applicantId) return;

    this.loading.set(true);
    this.documentsService.list(this.area(), applicantId).subscribe({
      next: (rows) => {
        this.documents.set(rows);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        const msg = err?.error?.message ?? 'Could not load documents.';
        this.toast.error(msg);
      },
    });
  }
}
